import React, { useEffect, useState, Fragment, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { steps, profiles, stepAnalysis, WHATSAPP_NUMBER, ProfileType } from './data';
type Screen = 'entry' | 'steps' | 'results';
export function TecnologiaPage() {
  const [currentScreen, setCurrentScreen] = useState<Screen>('entry');
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  // -1 means unanswered
  const [answers, setAnswers] = useState<number[]>(new Array(7).fill(-1));
  // Persist state
  useEffect(() => {
    const savedState = sessionStorage.getItem('tecnologiaState');
    if (savedState) {
      const parsed = JSON.parse(savedState);
      if (parsed.answers) setAnswers(parsed.answers);
      if (parsed.currentScreen) setCurrentScreen(parsed.currentScreen);
      if (parsed.currentStepIndex !== undefined) setCurrentStepIndex(parsed.currentStepIndex);
    }
  }, []);
  useEffect(() => {
    sessionStorage.setItem('tecnologiaState', JSON.stringify({
      answers,
      currentScreen,
      currentStepIndex
    }));
  }, [answers, currentScreen, currentStepIndex]);
  // Meta tags for standalone page
  useEffect(() => {
    document.title = 'Guía de Tecnología — Oscar Rodriguez';
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', 'noindex, nofollow');
    // Open Graph tags
    const setMetaTag = (property: string, content: string) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    setMetaTag('og:title', 'Guía: La secuencia correcta para implementar tecnología en tu negocio');
    setMetaTag('og:description', 'Por qué la mayoría se equivoca — y cómo elegir tecnología que realmente libere.');
  }, []);
  // Redirect if accessing results without completing
  useEffect(() => {
    if (currentScreen === 'results' && answers.includes(-1)) {
      setCurrentScreen('entry');
    }
  }, [currentScreen, answers]);
  const handleAnswer = (value: number) => {
    const newAnswers = [...answers];
    newAnswers[currentStepIndex] = value;
    setAnswers(newAnswers);
  };
  const handleNext = () => {
    if (currentStepIndex < 6) {
      setCurrentStepIndex(prev => prev + 1);
      window.scrollTo(0, 0);
    } else {
      setCurrentScreen('results');
      window.scrollTo(0, 0);
    }
  };
  const handlePrev = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };
  const renderBodyText = (text: string) => {
    // Handle bolding for Step 4 criteria
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index} className="font-bold text-white" data-id="element-293">
            {part.slice(2, -2)}
          </strong>;
      }
      // Handle newlines
      return part.split('\n').map((line, i, arr) => <Fragment key={`${index}-${i}`} data-id="element-294">
          {line}
          {i < arr.length - 1 && <br data-id="element-295" />}
        </Fragment>);
    });
  };
  const renderEntry = () => <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen flex flex-col items-center justify-center px-6 text-center py-12" data-id="element-296">
      <img src="https://cdn.magicpatterns.com/uploads/8iq9FdZPWhhg1moFGMfStg/Gemini_Generated_Image_vohteevohteevoht_(1)-Photoroom.png" alt="OR Logo" className="h-16 mb-8" data-id="element-297" />
      <p className="text-gold text-sm font-bold tracking-widest uppercase mb-6" data-id="element-298">
        GUÍA
      </p>
      <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6 leading-tight max-w-4xl" data-id="element-299">
        La secuencia correcta para implementar tecnología en tu negocio.
      </h1>
      <h2 className="text-xl md:text-2xl font-serif text-gold mb-8 max-w-2xl leading-relaxed" data-id="element-300">
        Por qué la mayoría de los dueños se equivoca — y cómo elegir tecnología
        que realmente libere.
      </h2>
      <p className="text-gray-light text-lg max-w-[600px] mb-12 leading-relaxed" data-id="element-301">
        7 pasos. En cada uno te haces una pregunta concreta sobre tu negocio. Al
        final, vas a saber dónde está tu punto más débil y qué hacer al
        respecto.
      </p>
      <button onClick={() => setCurrentScreen('steps')} className="bg-gold hover:bg-yellow-500 text-navy font-bold text-xl px-10 py-5 rounded transition-all duration-300 hover:scale-105 shadow-[0_0_20px_rgba(201,168,76,0.3)]" data-id="element-302">
        Comenzar guía →
      </button>
      <p className="text-sm text-gray-light/60 mt-6" data-id="element-303">
        Para dueños de negocio con operación activa. Toma 8 minutos.
      </p>
    </motion.div>;
  const renderStep = () => {
    const step = steps[currentStepIndex];
    const progress = (currentStepIndex + 1) / 7 * 100;
    const hasAnswered = answers[currentStepIndex] !== -1;
    return <motion.div key={currentStepIndex} initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: -20
    }} className="min-h-screen flex flex-col px-6 py-12 max-w-3xl mx-auto w-full" data-id="element-304">
        {/* Progress Bar */}
        <div className="w-full mb-16" data-id="element-305">
          <p className="text-xs text-gray-light mb-2 text-right uppercase tracking-wider" data-id="element-306">
            Paso {currentStepIndex + 1} de 7
          </p>
          <div className="w-full h-1 bg-navy-dark rounded-full overflow-hidden" data-id="element-307">
            <div className="h-full bg-gold transition-all duration-500" style={{
            width: `${progress}%`
          }} data-id="element-308" />
          </div>
        </div>

        <div className="flex-grow flex flex-col" data-id="element-309">
          {/* Step Header */}
          <div className="mb-12" data-id="element-310">
            <span className="block text-7xl md:text-8xl font-serif font-bold text-gold mb-6 opacity-90" data-id="element-311">
              0{step.id}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white leading-tight" data-id="element-312">
              {step.title}
            </h2>
          </div>

          {/* Step Body */}
          <div className="space-y-6 mb-16 max-w-[700px]" data-id="element-313">
            {step.body.map((paragraph, idx) => <p key={idx} className="text-lg text-white/90 font-serif leading-relaxed" data-id="element-314">
                {renderBodyText(paragraph as string)}
              </p>)}
          </div>

          {/* Evaluation Box */}
          <div className="bg-[#252540] border border-gold/30 rounded-xl p-8 mb-12" data-id="element-315">
            <p className="text-xl font-serif font-bold text-white mb-8 text-center" data-id="element-316">
              {step.evaluationQuestion}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-317">
              {step.options.map((opt, i) => {
              const isSelected = answers[currentStepIndex] === opt.value;
              return <button key={i} onClick={() => handleAnswer(opt.value)} className={`flex-1 py-4 px-6 rounded border transition-all duration-200 font-medium ${isSelected ? 'bg-gold border-gold text-navy' : 'bg-navy border-gold text-white hover:bg-[#2d2d4d]'}`} data-id="element-318">
                    {opt.text}
                  </button>;
            })}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center pt-6 border-t border-white/10" data-id="element-319">
          {currentStepIndex > 0 ? <button onClick={handlePrev} className="text-gray-light hover:text-white transition-colors px-4 py-2" data-id="element-320">
              ← Anterior
            </button> : <div data-id="element-321"></div>}

          <button onClick={handleNext} disabled={!hasAnswered} className={`font-bold px-8 py-3 rounded transition-all ${hasAnswered ? 'bg-gold text-navy hover:bg-yellow-500' : 'bg-navy-dark text-gray-light/30 cursor-not-allowed border border-white/10'}`} data-id="element-322">
            Siguiente →
          </button>
        </div>
      </motion.div>;
  };
  const renderResults = () => {
    const totalScore = answers.reduce((a, b) => a + b, 0);
    let profileKey: ProfileType = 'temprana';
    if (totalScore >= 5 && totalScore <= 9) profileKey = 'intermedia';
    if (totalScore >= 10 && totalScore <= 14) profileKey = 'madura';
    const profileData = profiles[profileKey];
    // Find weakest step (lowest score, first occurrence if tie)
    let minScore = 3; // higher than max possible 2
    let weakestStepIndex = 0;
    answers.forEach((score, index) => {
      if (score < minScore) {
        minScore = score;
        weakestStepIndex = index;
      }
    });
    const weakestStep = steps[weakestStepIndex];
    const weakestAnalysis = stepAnalysis[weakestStep.id];
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(profileData.whatsappMsg)}`;
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="min-h-screen py-20 px-6 max-w-3xl mx-auto" data-id="element-323">
        {/* Section A - Global Result */}
        <div className="text-center mb-16" data-id="element-324">
          <h1 className="text-2xl font-serif font-bold text-white mb-8" data-id="element-325">
            Tu nivel actual de implementación
          </h1>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-gold mb-6 leading-tight" data-id="element-326">
            {profileData.name}
          </h2>
          <p className="text-gray-light text-lg" data-id="element-327">{totalScore} de 14</p>
        </div>

        {/* Section B - Profile Analysis */}
        <div className="space-y-6 text-lg text-white font-serif leading-relaxed mb-20 max-w-[700px] mx-auto text-center" data-id="element-328">
          {profileData.analysis.map((p, i) => <p key={i} data-id="element-329">{p}</p>)}
        </div>

        {/* Section C - Weakest Step */}
        <div className="mb-20 bg-navy-dark p-8 md:p-10 rounded-xl border border-white/5" data-id="element-330">
          <h3 className="text-2xl font-serif font-bold text-gold mb-6" data-id="element-331">
            Tu punto más débil: Paso {weakestStep.id} — {weakestStep.title}
          </h3>
          <p className="text-white font-serif text-lg leading-relaxed" data-id="element-332">
            {weakestAnalysis}
          </p>
        </div>

        {/* Section D - CTA */}
        <div className="text-center border border-gold p-8 md:p-12 rounded-xl mb-20" data-id="element-333">
          <h2 className="text-3xl font-serif font-bold text-gold mb-4" data-id="element-334">
            ¿Quieres revisar tu caso específico?
          </h2>
          <p className="text-white font-sans text-lg mb-10 max-w-xl mx-auto" data-id="element-335">
            Te respondo personalmente. Sin formularios. Sin secuencias
            automáticas. Una conversación directa.
          </p>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-block bg-gold hover:bg-yellow-500 text-navy font-bold text-xl px-10 py-5 rounded transition-all duration-300 hover:scale-105 shadow-xl w-full sm:w-auto" data-id="element-336">
            Hablemos por WhatsApp →
          </a>
          <p className="text-sm text-gray-light mt-6" data-id="element-337">
            Para dueños de negocio con operación activa.
          </p>
        </div>

        {/* Section E - Footer */}
        <div className="text-center border-t border-white/10 pt-12 pb-8" data-id="element-338">
          <img src="https://cdn.magicpatterns.com/uploads/8iq9FdZPWhhg1moFGMfStg/Gemini_Generated_Image_vohteevohteevoht_(1)-Photoroom.png" alt="OR Logo" className="h-10 mx-auto mb-4 opacity-50" data-id="element-339" />
          <a href="https://instagram.com/oscarrodriguezm" target="_blank" rel="noopener noreferrer" className="text-gold hover:text-yellow-500 text-sm font-serif block mb-6" data-id="element-340">
            @oscarrodriguezm
          </a>
          <div className="flex justify-center gap-4 text-sm text-gray-light mb-4" data-id="element-341">
            <a href="https://instagram.com/oscarrodriguezm" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-id="element-342">
              Instagram
            </a>
            <span data-id="element-343">|</span>
            <span className="hover:text-white transition-colors cursor-pointer" data-id="element-344">
              Privacidad
            </span>
          </div>
          <p className="text-sm text-gray-light" data-id="element-345">
            © 2026 Oscar Rodriguez. Todos los derechos reservados.
          </p>
        </div>
      </motion.div>;
  };
  return <div className="min-h-screen bg-navy text-white font-sans selection:bg-gold/30 selection:text-white" data-id="element-346">
      <AnimatePresence mode="wait" data-id="element-347">
        {currentScreen === 'entry' && renderEntry()}
        {currentScreen === 'steps' && renderStep()}
        {currentScreen === 'results' && renderResults()}
      </AnimatePresence>
    </div>;
}